import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  makeStyles,
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const empty = { title: "", text: "", priority: "normal", active: true };

const AnnouncementModal = ({ open, onClose, announcement }) => {
  const classes = useStyles();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      announcement
        ? {
            title: announcement.title,
            text: announcement.text,
            priority: announcement.priority,
            active: announcement.active,
          }
        : empty
    );
  }, [open, announcement]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (announcement) {
        await api.put(`/announcements/${announcement.id}`, form);
      } else {
        await api.post("/announcements", form);
      }
      toast.success(i18n.t("announcements.toasts.saved"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t(announcement ? "announcementModal.title.edit" : "announcementModal.title.add")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <TextField
            className={classes.field}
            label={i18n.t("announcementModal.form.title")}
            value={form.title}
            onChange={(e) => set("title")(e.target.value)}
            variant="outlined"
            fullWidth
            required
            autoFocus
            inputProps={{ maxLength: 150 }}
          />
          <TextField
            className={classes.field}
            label={i18n.t("announcementModal.form.text")}
            value={form.text}
            onChange={(e) => set("text")(e.target.value)}
            variant="outlined"
            fullWidth
            required
            multiline
            minRows={5}
            inputProps={{ maxLength: 5000 }}
          />
          <TextField
            className={classes.field}
            select
            label={i18n.t("announcementModal.form.priority")}
            value={form.priority}
            onChange={(e) => set("priority")(e.target.value)}
            variant="outlined"
          >
            {["low", "normal", "high"].map((p) => (
              <MenuItem key={p} value={p}>
                {i18n.t(`announcements.priority.${p}`)}
              </MenuItem>
            ))}
          </TextField>
          <div>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={form.active}
                  onChange={(e) => set("active")(e.target.checked)}
                />
              }
              label={i18n.t("announcementModal.form.active")}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving} variant="outlined">
            {i18n.t("announcementModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={saving}
            className={classes.btnWrapper}
          >
            {i18n.t("announcementModal.buttons.ok")}
            {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AnnouncementModal;
